import { useEffect, useRef, useState, useCallback } from "react";
import { useGameClubContext } from "../Hook/GameClubContext";
import {
  GameClubMemberHeader,
  GameClubMemberHeaderItem,
  GameClubMemberTable,
  GameClubMemberTableItem,
  GameClubMemberTableItemMember,
  GameClubMemberWrapper,
} from "../Style/GameClubMemberStyles";
import {
  GameClubMemberResponse,
  getClubMember,
} from "../../../../api/game-club";
import { isArray } from "lodash";
import { LOCAL_STORAGE_ACCESS_KEY } from "../../../../utils/network";
import { getClubScore } from "./utils/rank";
import GameClubCalendar from "./component/GameClubCalendar";

interface GameClubMemberProps {
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isCalendarOpen: boolean) => void;
}

export const GameClubMember = ({
  isCalendarOpen,
  setIsCalendarOpen,
}: GameClubMemberProps) => {
  const { selectClubId, myClub } = useGameClubContext();
  const [list, setList] = useState<GameClubMemberResponse[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const listRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  // 페이징 관련 상태
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const handleStartDate = (date: string) => {
    setStartDate(date);
  };

  const handleEndDate = (date: string) => {
    setEndDate(date);
  };

  const utcTimeToYYMMDD = (utcTime: string) => {
    const date = new Date(utcTime);
    const year = date.getFullYear().toString().slice(-2); // 마지막 2자리
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}.${month}.${day}`;
  };

  // K, M 단위를 분리하여 렌더링하는 컴포넌트
  const ScoreDisplay = ({ value }: { value: number }) => {
    const scoreStr = getClubScore(value);
    const match = scoreStr.match(/^([\d.]+)([KM])$/);

    if (match) {
      const [, number, unit] = match;
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            lineHeight: "1",
            fontSize: "9px",
          }}
        >
          <div>{number}</div>
          <div style={{ fontSize: "8px", marginTop: "1px" }}>{unit}</div>
        </div>
      );
    }

    return <div>{scoreStr}</div>;
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    getList(startDate, endDate, 1, false);
  }, [startDate, endDate]);

  const getSortValue = () => {
    if (!sortKey) return null;

    const sortMap: { [key: string]: string } = {
      handCount: "hand",
      betAmount: "bet",
      winCount: "win",
      tournamentJoinCount: "tournament",
      joinedAt: "join",
    };

    const prefix = sortMap[sortKey];
    if (!prefix) return null;

    return `${prefix}_${sortDirection}`;
  };

  const getList = async (
    startDate?: string | null,
    endDate?: string | null,
    pageNum?: number,
    isAppend?: boolean
  ) => {
    if (!myClub || loading) return;

    setLoading(true);
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
    const accessToken = token ? token.replace("Bearer ", "") : "-";

    try {
      const sort = getSortValue();
      const currentPage = pageNum || page;

      const res = await getClubMember(
        myClub?.clubId,
        accessToken,
        startDate,
        endDate,
        sort,
        currentPage,
        20
      );

      const { members, total } = res.payload;
      const totalPages = Math.ceil(total / 20);

      if (isAppend) {
        setList((prevList) => [...prevList, ...members]);
      } else {
        setList(members);
      }

      setHasMore(currentPage < totalPages);
    } catch (e) {
      console.error(e);
      if (!isAppend) {
        setList([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }

    // 정렬 변경 시 페이지 리셋하고 맨 위로 스크롤
    setPage(1);
    setHasMore(true);
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  };

  // 무한 스크롤 핸들러
  const handleScroll = useCallback(() => {
    if (!tableRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = tableRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 200) {
      const nextPage = page + 1;
      setPage(nextPage);
      getList(startDate, endDate, nextPage, true);
    }
  }, [loading, hasMore, page, startDate, endDate]);

  // 스크롤 이벤트 리스너 등록
  useEffect(() => {
    const currentRef = tableRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
      return () => {
        currentRef.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    getList(startDate, endDate, 1, false);
  }, [selectClubId, sortKey, sortDirection]);

  return (
    <GameClubMemberWrapper ref={listRef}>
      <GameClubMemberHeader>
        <GameClubMemberHeaderItem width={26}></GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={10}
          onClick={() => handleSort("handCount")}
        >
          <div className="name">핸드</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "handCount" && sortDirection === "desc"
                ? "rotate"
                : ""
            }
          />
        </GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={10}
          onClick={() => handleSort("betAmount")}
        >
          <div className="name">베팅</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "betAmount" && sortDirection === "desc"
                ? "rotate"
                : ""
            }
          />
        </GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={10}
          onClick={() => handleSort("winCount")}
        >
          <div className="name">승리</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "winCount" && sortDirection === "desc" ? "rotate" : ""
            }
          />
        </GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={10}
          onClick={() => handleSort("tournamentJoinCount")}
        >
          <div className="name">참여</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "tournamentJoinCount" && sortDirection === "desc"
                ? "rotate"
                : ""
            }
          />
        </GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={12}
          onClick={() => handleSort("joinedAt")}
        >
          <div className="name">가입일</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "joinedAt" && sortDirection === "desc" ? "rotate" : ""
            }
          />
        </GameClubMemberHeaderItem>
      </GameClubMemberHeader>
      <GameClubMemberTable ref={tableRef}>
        {isArray(list) &&
          list.map((item, index) => (
            <GameClubMemberTableItem key={index}>
              <GameClubMemberTableItemMember>
                <div className="name">{item.nickname}</div>
                <div className={`role ${item.role}`}>
                  {item.role === "LEADER" ? "클럽장" : "클럽원"}
                </div>
              </GameClubMemberTableItemMember>
              <div className="count">{getClubScore(item.handCount)}</div>
              <div className="count">{getClubScore(item.betAmount)}</div>
              <div className="count">{getClubScore(item.winCount)}</div>
              <div className="count">
                {getClubScore(item.tournamentJoinCount)}
              </div>
              <div className="date">{utcTimeToYYMMDD(item.joinedAt)}</div>
            </GameClubMemberTableItem>
          ))}
      </GameClubMemberTable>
      {isCalendarOpen && (
        <GameClubCalendar
          startDate={startDate}
          endDate={endDate}
          handleStartDate={handleStartDate}
          handleEndDate={handleEndDate}
          onClose={() => setIsCalendarOpen(false)}
        />
      )}
    </GameClubMemberWrapper>
  );
};
